import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowLeftIcon, BriefcaseIcon, CheckIcon, DownloadIcon, EyeIcon, FileTextIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { Button, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { InvoiceLinesEditor } from '../../components/admin/InvoiceLinesEditor';
import { InvoicePdfPreview } from '../../components/admin/InvoicePdfPreview';
import { InvoiceStatusBadge } from '../../components/admin/InvoiceStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { downloadInvoicePdf } from '../../utils/invoicePdf';
import { isOverdue } from '../../utils/invoice';
import { formatKr } from '../../utils/quote';
import { formatDate } from '../../utils/format';
import { invoiceStatuses } from '../../types/invoice';
import type { InvoiceLine, InvoiceStatus } from '../../types/invoice';
const ease = [0.23, 1, 0.32, 1] as const;
const SENT_WARNING = 'Denne faktura er allerede sendt. Ændringer kan betyde, at kunden har en tidligere version.';
export function InvoiceDetail() {
  const {
    invoiceId
  } = useParams();
  const navigate = useNavigate();
  const {
    invoices,
    cases,
    quotes,
    updateInvoice,
    setInvoiceStatus,
    deleteInvoice,
    authLoading,
    settings
  } = useAdminData();
  const invoice = invoices.find(entry => entry.id === invoiceId);
  const [form, setForm] = useState({
    customerName: '',
    customerAddress: '',
    customerPostalCode: '',
    customerCity: '',
    customerEmail: '',
    customerPhone: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    paymentTerms: '',
    reference: '',
    bankName: '',
    registrationNumber: '',
    accountNumber: '',
    paymentReference: '',
    paymentText: '',
    description: '',
    customerNote: '',
    internalNote: ''
  });
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [preview, setPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!invoice) return;
    setForm({
      customerName: invoice.customerName,
      customerAddress: invoice.customerAddress,
      customerPostalCode: invoice.customerPostalCode,
      customerCity: invoice.customerCity,
      customerEmail: invoice.customerEmail,
      customerPhone: invoice.customerPhone,
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      paymentTerms: invoice.paymentTerms,
      reference: invoice.reference,
      bankName: invoice.bankName,
      registrationNumber: invoice.registrationNumber,
      accountNumber: invoice.accountNumber,
      paymentReference: invoice.paymentReference,
      paymentText: invoice.paymentText,
      description: invoice.description,
      customerNote: invoice.customerNote,
      internalNote: invoice.internalNote
    });
    setLines(invoice.lines);
  }, [invoice?.id]);
  if (!invoice) return <Navigate to="/admin/fakturaer" replace data-id="element-929" data-mp="18ztg6b5mwvhn" />;
  const linkedCase = cases.find(item => item.id === invoice.caseId);
  const linkedQuote = quotes.find(item => item.id === invoice.quoteId);
  const overdue = isOverdue(invoice);
  function change<K extends keyof typeof form>(key: K, value: string) {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    setSaved(false);
  }
  async function handleSave() {
    if (saving || !invoice) return;
    if (authLoading) {
      setError('Sessionen indlæses stadig. Prøv igen om et øjeblik.');
      return;
    }
    // En sendt faktura må aldrig ændres i stilhed.
    if (invoice.status === 'Sendt' && !window.confirm(`${SENT_WARNING}\n\nVil du gemme alligevel?`)) {
      return;
    }
    setSaving(true);
    setError('');
    const result = await updateInvoice(invoice.id, {
      ...form,
      lines
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'Fakturaen kunne ikke gemmes.');
      return;
    }
    setSaved(true);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-930" data-mp="10286ypf8elxn">
      <Link to="/admin/fakturaer" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-931" data-mp="104umpcca07qy">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-932" data-mp="1a2ijs8xctrca" />
        Fakturaer
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-933" data-mp="150t4ui70gtix">
        <div data-id="element-934" data-mp="2lf1fpfif5eeq">
          <p className="text-sm text-ink-muted" data-id="element-935" data-mp="2let2tnmaq93j">{invoice.invoiceNumber}</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-936" data-mp="2gg81fkvb5jdi">
            {invoice.customerName || 'Faktura'}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-937" data-mp="2t05b6qdxnb9g">
            {formatKr(invoice.total)} inkl. moms · forfald {formatDate(invoice.dueDate)}
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end" data-id="element-938" data-mp="2j1pgkdfp5jv7">
          <InvoiceStatusBadge status={overdue ? 'Forfalden' : invoice.status} data-id="element-939" data-mp="2j3s6yykrhf0g" />
          <SelectField id="faktura-status" label="Status" className="w-56" value={invoice.status} onChange={async event => {
          setWorking('status');
          setError('');
          const result = await setInvoiceStatus(invoice.id, event.target.value as InvoiceStatus);
          setWorking('');
          if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
        }} options={invoiceStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-940" data-mp="36rtnous3g1qy" />
        </div>
      </div>

      {invoice.status === 'Sendt' && <p className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-941" data-mp="4651eyh7w0mh">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-942" data-mp="3vbn6moot3wag" />
          {SENT_WARNING}
        </p>}

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_0.5fr]" data-id="element-943" data-mp="3qcabzhrp1kxb">
        <div className="space-y-5" data-id="element-944" data-mp="3leilms6p66v8">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-945" data-mp="3lfjwownpqtcy">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-946" data-mp="3gm7k5kwb6d0k">Kunde</h2>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-947" data-mp="3ckvu5e9ivpru">
              <TextField id="fa-navn" label="Navn" className="sm:col-span-2" value={form.customerName} onChange={event => change('customerName', event.target.value)} data-id="element-948" data-mp="3cjb0dqv18av1" />
              <TextField id="fa-adresse" label="Adresse" className="sm:col-span-2" value={form.customerAddress} onChange={event => change('customerAddress', event.target.value)} data-id="element-949" data-mp="2xoxyry7fapgo" />
              <TextField id="fa-postnr" label="Postnummer" value={form.customerPostalCode} onChange={event => change('customerPostalCode', event.target.value)} data-id="element-950" data-mp="2xjoylyo5hf35" />
              <TextField id="fa-by" label="By" value={form.customerCity} onChange={event => change('customerCity', event.target.value)} data-id="element-951" data-mp="24rvj69fx0xn0" />
              <TextField id="fa-mail" label="E-mail" type="email" value={form.customerEmail} onChange={event => change('customerEmail', event.target.value)} data-id="element-952" data-mp="2jivzt1qtfxu8" />
              <TextField id="fa-tlf" label="Telefon" type="tel" value={form.customerPhone} onChange={event => change('customerPhone', event.target.value)} data-id="element-953" data-mp="1r1a651d57hx1" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-954" data-mp="3f80pukln155o">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-955" data-mp="3a7lbpqtj7tmn">
              Fakturaoplysninger
            </h2>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-956" data-mp="3o6cqzgk4rlcd">
              <TextField id="fa-nummer" label="Fakturanummer" value={form.invoiceNumber} onChange={event => change('invoiceNumber', event.target.value)} data-id="element-957" data-mp="3k6u62ksb7a9j" />
              <TextField id="fa-reference" label="Reference" value={form.reference} onChange={event => change('reference', event.target.value)} data-id="element-958" data-mp="3sz5vc3bosrnx" />
              <TextField id="fa-dato" label="Fakturadato" type="date" value={form.invoiceDate} onChange={event => change('invoiceDate', event.target.value)} data-id="element-959" data-mp="6pl2678bvg6x" />
            </div>
            {linkedCase && <p className="mt-5 text-sm text-ink-muted" data-id="element-960" data-mp="ghe5qxu82lux">
                Sagsreference: Sag #{linkedCase.number} — {linkedCase.title}
              </p>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-961" data-mp="6n438nbkg76h">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-962" data-mp="bhi2j0tcmygf">Fakturalinjer</h2>
            <div className="mt-5" data-id="element-963" data-mp="3tvhjdstu9808">
              <InvoiceLinesEditor lines={lines} onChange={next => {
              setLines(next);
              setSaved(false);
            }} data-id="element-964" data-mp="3ts3jcr8ndavy" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-965" data-mp="3ots6e7n22k9i">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-966" data-mp="jbpj8j170wr8">Betaling</h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-967" data-mp="ob27n87pqbvx">
              Står på kundens PDF. Tomme felter vises ikke.
            </p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-968" data-mp="9fcqbndh2mxj">
              <TextField id="fa-bank" label="Bank" value={form.bankName} onChange={event => change('bankName', event.target.value)} data-id="element-969" data-mp="ecw30iuiurnd" />
              <TextField id="fa-reference-betaling" label="Betalingsreference" value={form.paymentReference} onChange={event => change('paymentReference', event.target.value)} data-id="element-970" data-mp="17231jhtz7h8m" />
              <TextField id="fa-regnr" label="Reg.nr." inputMode="numeric" value={form.registrationNumber} onChange={event => change('registrationNumber', event.target.value)} data-id="element-971" data-mp="x9xdukarblz8" />
              <TextField id="fa-kontonr" label="Kontonr." inputMode="numeric" value={form.accountNumber} onChange={event => change('accountNumber', event.target.value)} data-id="element-972" data-mp="3ah5dcagwkyr1" />
              <TextField id="fa-betaling" label="Betalingsbetingelser" value={form.paymentTerms} onChange={event => change('paymentTerms', event.target.value)} data-id="element-973" data-mp="3f96izdjkfr83" />
              <TextField id="fa-forfald" label="Forfaldsdato" type="date" value={form.dueDate} onChange={event => change('dueDate', event.target.value)} data-id="element-974" data-mp="2mmlr8bz6hd40" />
            </div>
            <TextArea id="fa-betalingstekst" label="Betalingstekst" hint="Valgfri. Fx hvordan beløbet indbetales." className="mt-5" rows={2} value={form.paymentText} onChange={event => change('paymentText', event.target.value)} data-id="element-975" data-mp="ft9yxmhv8iwn" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-976" data-mp="3ele29n1b5g0r">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-977" data-mp="3ej9nub5s8ym8">Bemærkninger</h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-978" data-mp="3ikv84hkncf6g">
              Tekst til kunden, som står nederst på fakturaen. Er feltet tomt, vises afsnittet ikke.
            </p>
            <TextArea id="fa-kundenote" label="Bemærkninger til kunden" className="mt-5" rows={4} value={form.customerNote} onChange={event => change('customerNote', event.target.value)} data-id="element-979" data-mp="2qwbbnpop4cu4" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-980" data-mp="235yvjrjar9sg">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-981" data-mp="276hqvvkeexze">Tekst på faktura</h2>
            <TextArea id="fa-beskrivelse" label="Beskrivelse" className="mt-5" rows={4} value={form.description} onChange={event => change('description', event.target.value)} data-id="element-982" data-mp="1zn05ecnlfy07" />
            <TextArea id="fa-internnote" label="Intern note" hint="Vises aldrig på kundens PDF." className="mt-5" rows={3} value={form.internalNote} onChange={event => change('internalNote', event.target.value)} data-id="element-983" data-mp="1lpbhsx256crg" />
          </section>

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-984" data-mp="12y0usqzjek4u">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-985" data-mp="12w7kn1ppths3" />
              {error}
            </p>}

          <div className="flex flex-wrap items-center gap-3" data-id="element-986" data-mp="szl1qigy90sp">
            <Button size="lg" onClick={handleSave} disabled={saving} data-id="element-987" data-mp="t27a2kbnzkbz">
              {saving ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-988" data-mp="2y0xhxtvlpfl7" />
                  Gemmer …
                </> : 'Gem kladde'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => setPreview(true)} data-id="element-989" data-mp="2oam2pb6gvjz0">
              <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-990" data-mp="2abuqxvf2uae1" />
              Forhåndsvis
            </Button>
            <Button variant="secondary" size="lg" disabled={downloading} onClick={async () => {
            setDownloading(true);
            setError('');
            try {
              await downloadInvoicePdf(invoice, settings);
            } catch {
              setError('PDF’en kunne ikke dannes. Prøv igen.');
            }
            setDownloading(false);
          }} data-id="element-991" data-mp="2edptf38qutte">
              {downloading ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-992" data-mp="1o3pao8g934py" />
                  Danner PDF …
                </> : <>
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-993" data-mp="1xu26iwtimkbe" />
                  Download PDF
                </>}
            </Button>
            {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-994" data-mp="26t5otykuyvou">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-995" data-mp="21uuelmqnij63">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-996" data-mp="2gqtn2yhoalbr" />
                </span>
                Ændringerne er gemt
              </p>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-997" data-mp="mdbuuy6bp0ae">
            <Button variant="secondary" size="md" disabled={working === 'sent' || invoice.status === 'Sendt'} onClick={async () => {
            setWorking('sent');
            setError('');
            const result = await setInvoiceStatus(invoice.id, 'Sendt');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-998" data-mp="mfy37011fjto">
              Marker som sendt
            </Button>
            <Button variant="secondary" size="md" disabled={working === 'paid' || invoice.status === 'Betalt'} onClick={async () => {
            setWorking('paid');
            setError('');
            const result = await setInvoiceStatus(invoice.id, 'Betalt');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-999" data-mp="1q20dqphzeyk0">
              Marker som betalt
            </Button>
            <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-line px-4 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-1000" data-mp="2ds4ix1atj6d4">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-1001" data-mp="3od0xbrjr5w2c" />
              Slet faktura
            </button>
          </div>
        </div>

        <aside className="space-y-5" data-id="element-1002" data-mp="kuqv2cm4cxd8">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1003" data-mp="ktpg3x1zfqq8">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1004" data-mp="fwpa1m54unz4">Beløb</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-1005" data-mp="b3cyacljybns">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1006" data-mp="664de490rtiu">
                <dt className="text-ink-soft" data-id="element-1007" data-mp="63ql235q4vez">Ekskl. moms</dt>
                <dd className="font-medium text-ink" data-id="element-1008" data-mp="13lqmrll00la7">{formatKr(invoice.subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1009" data-mp="ysp91gwj2rs7">
                <dt className="text-ink-soft" data-id="element-1010" data-mp="76fd1vrdamng">Moms</dt>
                <dd className="font-medium text-ink" data-id="element-1011" data-mp="74kv8qbe7anl">{formatKr(invoice.vatAmount)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1012" data-mp="bz8latm1od4a">
                <dt className="font-medium text-ink" data-id="element-1013" data-mp="3ucyaltluc7ga">Total</dt>
                <dd className="font-display text-lg font-bold text-ink" data-id="element-1014" data-mp="34kbcq5iy795">{formatKr(invoice.total)}</dd>
              </div>
            </dl>
          </section>

          {linkedCase && <section className="rounded-card border border-line bg-white p-6" data-id="element-1015" data-mp="uvzx6hz9bpyb">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1016" data-mp="utx9w5phoaxa">
                Tilknyttet sag
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-1017" data-mp="luildbsvuaff">
                Sag #{linkedCase.number} — {linkedCase.title}
              </p>
              <Link to={`/admin/sager/${linkedCase.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-1018" data-mp="31lekdpqttt4s">
                <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-1019" data-mp="2z1i5n3ffo5sv" />
                Åbn sag
              </Link>
            </section>}

          {linkedQuote && <section className="rounded-card border border-line bg-white p-6" data-id="element-1020" data-mp="382pxb2ng28dz">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1021" data-mp="333vdbxdqqdsa">
                Tilknyttet tilbud
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-1022" data-mp="2ffc3veiyfuym">{linkedQuote.quoteNumber}</p>
              <Link to={`/admin/tilbud/${linkedQuote.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-1023" data-mp="2bdi7hifsld49">
                <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-1024" data-mp="2p5ygr0zae7dm" />
                Åbn tilbud
              </Link>
            </section>}

          {invoice.customerId && <section className="rounded-card border border-line bg-white p-6" data-id="element-1025" data-mp="1wlq13vxz79eo">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1026" data-mp="1ro6nmy93r0ns">Kunde</h2>
              <Link to={`/admin/kunder/${invoice.customerId}`} className="mt-3 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-1027" data-mp="o539yxvtq95m">
                Se kundeprofil
              </Link>
            </section>}
        </aside>
      </div>

      {preview && <InvoicePdfPreview invoice={invoice} onClose={() => setPreview(false)} data-id="element-1028" data-mp="t1jy3s30wnip" />}

      {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-faktura-titel" data-id="element-1029" data-mp="12tg6px2qjw16">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-1030" data-mp="1bue0znndgp6k">
            <h2 id="slet-faktura-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-1031" data-mp="3dn2662jktmoz">
              Er du sikker på, at du vil slette denne faktura?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-1032" data-mp="1y4e2kq1dtoz9">
              {invoice.invoiceNumber} slettes permanent.
            </p>
            {linkedCase && <p className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1033" data-mp="1obfauzal9ibf">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1034" data-mp="1abnuirwlnpu9" />
                Fakturaen er knyttet til sag #{linkedCase.number}. Sagen bliver ikke slettet, men
                mister henvisningen til fakturaen.
              </p>}
            {error && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-1035" data-mp="15e4cri0p0uxk">
                {error}
              </p>}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-1036" data-mp="2uqzelsvjv8ib">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setError('');
            const result = await deleteInvoice(invoice.id);
            setDeleting(false);
            if (!result.ok) {
              setError(result.error ?? 'Fakturaen kunne ikke slettes. Prøv igen.');
              return;
            }
            navigate('/admin/fakturaer');
          }} data-id="element-1037" data-mp="2zr3yztzkwndt">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1038" data-mp="37awfsa03zs2n" />
                    Sletter …
                  </> : 'Slet faktura'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirmingDelete(false)} data-id="element-1039" data-mp="3n4ik11d57l4">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}
    </div>;
}